import React, { useRef, useEffect, useMemo, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Text, Billboard } from "@react-three/drei";
import { EffectComposer, Bloom, Vignette } from "@react-three/postprocessing";
import * as THREE from "three";
import { Link } from "react-router-dom";

// ── Homepage palette ────────────────────────────────────────────────────────
// Reads the --home-* theme variables from App.css so this scene stays in sync
// with the rest of the homepage without baking hex values in here.
const getHomeColor = (cssVarName) =>
  getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim();

const SAND = getHomeColor("--home-bg");
const MAROON = getHomeColor("--home-primary");

const TITLE_FONT = "/fonts/milker.otf";
const BODY_FONT = "/fonts/spartan.ttf";

// --home-accent / --home-cta are too pale to read against the SAND background,
// so chip/mote colors are readable shades derived from --home-primary instead.
const MAROON_LIGHT = `#${new THREE.Color(MAROON).offsetHSL(0, 0, 0.16).getHexString()}`;
const MAROON_DARK = `#${new THREE.Color(MAROON).offsetHSL(0, 0, -0.14).getHexString()}`;

const CHIP_COLORS = [MAROON, MAROON_DARK, MAROON_LIGHT];

// ── Skills by category (resume + homepage MarqueeMenu, mixed) ───────────────
const STATIONS = [
  {
    title: "FRONTEND",
    center: [0, 0, 0],
    skills: [
      "Next.js",
      "React",
      "TypeScript",
      "Tailwind CSS",
      "GSAP",
      "Framer Motion",
      "TanStack Query",
      "Three.js / R3F",
      "WebGL / GLSL",
    ],
  },
  {
    title: "BACKEND",
    center: [-13, 3, -46],
    skills: [
      "Node.js",
      "Express.js",
      "REST APIs",
      "PostgreSQL",
      "TypeORM",
      "WebSockets",
      "Stripe",
      "Razorpay",
    ],
  },
  {
    title: "PLATFORMS & DEVOPS",
    center: [12, -2, -92],
    skills: [
      "Vercel",
      "GCP",
      "Docker",
      "GitHub Actions",
      "Mapbox",
      "Supabase",
      "Git",
    ],
  },
  {
    title: "AI & EXPERIMENTAL",
    center: [-10, 4, -138],
    skills: [
      "RAG Pipelines",
      "LLM Embeddings",
      "Vector Databases",
      "Agentic Workflows",
      "MCP",
      "Prompt Engineering",
      "ElevenLabs",
      "Kokoro",
      "Deepgram"
    ],
  },
  {
    title: "CURRENTLY EXPLORING",
    center: [9, -3, -184],
    skills: [
      "LLM Fine-tuning",
      "Distributed Systems",
      "Vector Search",
      "Computer Vision",
      "Audio DSP",
    ],
  },
];

// Layout presets — desktop is wide, mobile is narrow with smaller type.
const LAYOUT = {
  desktop: { title: 1.6, chip: 0.62, cols: 3, colW: 6.8, rowH: 1.7, titleGap: 2.2 },
  mobile: { title: 1.0, chip: 0.46, cols: 2, colW: 4.6, rowH: 1.3, titleGap: 2.5 },
};

// Even, centered grid — each row is centered, the block is centered on y=0.
// z is fixed at 0 so every chip shares one focal plane.
function gridPositions(count, cfg) {
  const { cols, colW, rowH } = cfg;
  const rows = Math.ceil(count / cols);
  const totalH = (rows - 1) * rowH;
  const pos = [];
  for (let i = 0; i < count; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const itemsInRow = r === rows - 1 ? count - r * cols : cols;
    const rowWidth = (itemsInRow - 1) * colW;
    pos.push([c * colW - rowWidth / 2, totalH / 2 - r * rowH, 0]);
  }
  return { pos, titleY: totalH / 2 + cfg.titleGap };
}

function Station({ title, center, skills, cfg, isMobile }) {
  const { pos, titleY } = gridPositions(skills.length, cfg);
  return (
    <group position={center}>
      <Billboard>
        <Text
          font={TITLE_FONT}
          fontSize={cfg.title}
          color={MAROON}
          anchorX="center"
          anchorY="middle"
          textAlign="center"
          position={[0, titleY, 0]}
          letterSpacing={-0.02}
        >
          {isMobile ? title.replace(/ /g, "\n") : title}
        </Text>
      </Billboard>

      {skills.map((skill, i) => (
        <Billboard key={skill} position={pos[i]}>
          <Text
            font={BODY_FONT}
            fontSize={cfg.chip}
            color={CHIP_COLORS[i % CHIP_COLORS.length]}
            anchorX="center"
            anchorY="middle"
          >
            {skill}
          </Text>
        </Billboard>
      ))}
    </group>
  );
}

// Drifting dust motes for atmosphere.
function Motes() {
  const ref = useRef();
  const { positions, geom } = useMemo(() => {
    const count = 260;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 60;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 40;
      positions[i * 3 + 2] = -Math.random() * 170 + 20;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    return { positions, geom };
  }, []);

  useFrame((state) => {
    if (ref.current) ref.current.rotation.y = state.clock.elapsedTime * 0.01;
  });

  return (
    <points ref={ref} geometry={geom}>
      <pointsMaterial
        size={0.16}
        color={MAROON_LIGHT}
        transparent
        opacity={0.5}
        sizeAttenuation
        depthWrite={false}
      />
    </points>
  );
}

// Soft cloud sprites — billboarded, single small canvas texture, no lighting.
// A handful of transparent quads = negligible GPU cost.
function Clouds() {
  const texture = useMemo(() => {
    const size = 128;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d");
    const g = ctx.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2
    );
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.4, "rgba(255,250,240,0.7)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    return new THREE.CanvasTexture(canvas);
  }, []);

  // Keep clouds in the top/bottom periphery so they frame, not cover, the text.
  const clouds = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 22; i++) {
      const sx = Math.random() < 0.5 ? 1 : -1;
      const sy = Math.random() < 0.5 ? 1 : -1;
      arr.push({
        // Hug the far X/Y periphery (screen corners) so even a cloud that drifts
        // close to the camera glows at the edge instead of veiling the text.
        position: [
          sx * (30 + Math.random() * 26),
          sy * (14 + Math.random() * 14),
          10 - Math.random() * 210,
        ],
        scale: 16 + Math.random() * 16,
      });
    }
    return arr;
  }, []);

  return (
    <group>
      {clouds.map((c, i) => (
        <sprite
          key={i}
          position={c.position}
          scale={[c.scale * 1.7, c.scale, 1]}
          renderOrder={-1}
        >
          <spriteMaterial
            map={texture}
            transparent
            opacity={0.28}
            depthWrite={false}
          />
        </sprite>
      ))}
    </group>
  );
}

// Camera rig: flies a smooth spline through the stations, driven by scroll.
function CameraRig({ progressRef }) {
  const { camera } = useThree();
  const smoothed = useRef(0);
  const lookTarget = useRef(new THREE.Vector3());

  const { camCurve, lookCurve } = useMemo(() => {
    const centers = STATIONS.map((s) => new THREE.Vector3(...s.center));
    // Camera viewpoints sit in front of and slightly above each station, with
    // a gentle alternating sway for a flowing, cinematic flight.
    const camPts = STATIONS.map((s, i) => {
      const sway = i % 2 === 0 ? 3 : -3;
      return new THREE.Vector3(
        s.center[0] + sway,
        s.center[1] + 2.5,
        s.center[2] + 20
      );
    });

    // Progress 0 → 1 rests on the first station and ends on the last.
    const camCurve = new THREE.CatmullRomCurve3(camPts);
    const lookCurve = new THREE.CatmullRomCurve3(centers);
    return { camCurve, lookCurve };
  }, []);

  useFrame(() => {
    const target = progressRef.current;
    smoothed.current += (target - smoothed.current) * 0.03;
    const t = THREE.MathUtils.clamp(smoothed.current, 0, 1);

    // getPoint (uniform) — passes exactly through control point i at t=i/(n-1),
    // so each snap fraction rests precisely on its station. (getPointAt would
    // arc-length-remap and land between stations.)
    camCurve.getPoint(t, camera.position);
    lookCurve.getPoint(t, lookTarget.current);
    camera.lookAt(lookTarget.current);
  });

  return null;
}

function Scene({ progressRef, cfg }) {
  return (
    <>
      <color attach="background" args={[SAND]} />
      <fog attach="fog" args={[SAND, 16, 62]} />
      <ambientLight intensity={1.1} />
      <CameraRig progressRef={progressRef} />
      <Clouds />
      <Motes />
      {STATIONS.map((s) => (
        <Station key={s.title} {...s} cfg={cfg} isMobile={cfg === LAYOUT.mobile} />
      ))}
      <EffectComposer>
        <Bloom intensity={0.35} luminanceThreshold={0.55} luminanceSmoothing={0.6} />
        <Vignette eskil={false} offset={0.2} darkness={0.55} />
      </EffectComposer>
    </>
  );
}

export default function SkillsFlythrough() {
  const progressRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 768
  );
  const cfg = isMobile ? LAYOUT.mobile : LAYOUT.desktop;


  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Snap scrolling: one deliberate gesture steps to the next category, then the
  // camera dwells there (input locked ~1.4s) before another step is allowed.
  // A delta threshold + momentum reset keeps trackpads from over-scrolling.
  useEffect(() => {
    const n = STATIONS.length;
    let index = 0;
    let accum = 0;
    let lockUntil = 0;
    let lastEvent = 0;

    const step = (dir) => {
      const next = THREE.MathUtils.clamp(index + dir, 0, n - 1);
      accum = 0;
      if (next === index) return;
      index = next;
      progressRef.current = next / (n - 1);
      setActiveIndex(next);
      lockUntil = performance.now() + 900;
    };

    const feed = (delta, threshold) => {
      const now = performance.now();
      if (now - lastEvent > 180) accum = 0; // drop stale momentum
      lastEvent = now;
      if (now < lockUntil) return; // dwelling on the current category
      accum += delta;
      if (Math.abs(accum) >= threshold) step(Math.sign(accum));
    };

    const onWheel = (e) => {
      e.preventDefault();
      let d = e.deltaY;
      if (e.deltaMode === 1) d *= 16; // lines → px
      else if (e.deltaMode === 2) d *= window.innerHeight; // pages → px
      feed(d, 80);
    };

    let lastY = null;
    const onTouchStart = (e) => {
      lastY = e.touches[0].clientY;
      accum = 0;
    };
    const onTouchMove = (e) => {
      if (lastY === null) return;
      const dy = lastY - e.touches[0].clientY;
      lastY = e.touches[0].clientY;
      feed(dy, 70);
    };
    const onTouchEnd = () => (lastY = null);

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  return (
    <div className="bg-[#FBDB93]">
      <div className="fixed inset-0 z-0 bg-[#FBDB93]">
        <Canvas
          key={isMobile ? "m" : "d"}
          dpr={[1, 2]}
          camera={{
            fov: isMobile ? 62 : 45,
            near: 0.1,
            far: 200,
            position: [0, 1, 30],
          }}
          style={{ background: SAND }}
        >
          <Scene progressRef={progressRef} cfg={cfg} />
        </Canvas>
      </div>

      {/* Overlay UI */}
      <Link
        to="/"
        className="fixed top-8 left-8 z-10 font-raleway text-sm tracking-wide text-[var(--home-primary)] hover:opacity-60 transition-opacity"
      >
        ← home
      </Link>

      <div className="fixed top-8 right-8 z-10 font-raleway text-sm tracking-[0.25em] text-[var(--home-primary)]">
        SKILLS
      </div>

      {/* Category ticker + progress */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <div className={`font-milker text-[var(--home-primary)] text-base md:text-lg tracking-wide text-center${isMobile ? " whitespace-pre-line" : ""}`}>
          {isMobile ? STATIONS[activeIndex].title.replace(/ /g, "\n") : STATIONS[activeIndex].title}
        </div>
        <div className="flex gap-2">
          {STATIONS.map((s, i) => (
            <span
              key={s.title}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === activeIndex
                  ? "w-8 bg-[var(--home-primary)]"
                  : "w-1.5 bg-[var(--home-accent)]/40"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Scroll hint, only at the very top */}
      {activeIndex === 0 && (
        <div className="fixed bottom-28 left-1/2 -translate-x-1/2 z-10 font-raleway text-xs text-[var(--home-primary)] animate-bounce">
          scroll to fly ↓
        </div>
      )}
    </div>
  );
}
